import React, { useState, useEffect } from 'react';
import { whatsappApi } from '../services/whatsappApi';
import './WhatsAppMessenger.css';

const WhatsAppMessenger = () => {
  const [formData, setFormData] = useState({
    phones: '', // comma-separated numbers
    message: '',
    fileUrl: '',
    fileName: ''
  });
  const [loading, setLoading] = useState(false);
  const [accountState, setAccountState] = useState(null);
  const [response, setResponse] = useState(null);
  const [activeTab, setActiveTab] = useState('message');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthentication();
    checkAccountState();
  }, []);

  const checkAuthentication = () => {
    const token = localStorage.getItem('auth_token');
    setIsAuthenticated(!!token);
  };

  const checkAccountState = async () => {
    if (!isAuthenticated) return;
    try {
      const state = await whatsappApi.getAccountState();
      setAccountState(state);
    } catch (error) {
      console.error('Error checking account state:', error);
      if (error.error === 'Unauthenticated.') {
        setIsAuthenticated(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      setResponse({ success: false, error: 'Please log in to send messages' });
      return;
    }

    if (!formData.phones || !formData.message) {
      setResponse({ success: false, error: 'Phone(s) and message are required' });
      return;
    }

    const numbers = formData.phones.split(',').map(n => n.trim());
    if (numbers.length === 0) {
      setResponse({ success: false, error: 'Invalid phone numbers' });
      return;
    }

    setLoading(true);
    setResponse(null);

    const results = [];

    for (let phone of numbers) {
      try {
        const result = await whatsappApi.sendMessage(phone, formData.message);
        results.push({ phone, success: true, data: result });
      } catch (error) {
        results.push({ phone, success: false, error: error.message || error.error });
      }
    }

    setResponse({ success: true, data: results, message: 'Messages sent individually' });
    setFormData(prev => ({ ...prev, message: '' }));
    setLoading(false);
  };

  const handleSendFile = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      setResponse({ success: false, error: 'Please log in to send files' });
      return;
    }

    if (!formData.phones || !formData.fileUrl || !formData.fileName) {
      setResponse({ success: false, error: 'Phone(s), file URL, and file name are required' });
      return;
    }

    const numbers = formData.phones.split(',').map(n => n.trim());
    if (numbers.length === 0) {
      setResponse({ success: false, error: 'Invalid phone numbers' });
      return;
    }

    setLoading(true);
    setResponse(null);

    const results = [];

    for (let phone of numbers) {
      try {
        const result = await whatsappApi.sendFile(phone, formData.fileUrl, formData.fileName);
        results.push({ phone, success: true, data: result });
      } catch (error) {
        results.push({ phone, success: false, error: error.message || error.error });
      }
    }

    setResponse({ success: true, data: results, message: 'Files sent individually' });
    setFormData(prev => ({ ...prev, fileUrl: '', fileName: '' }));
    setLoading(false);
  };

  if (!isAuthenticated) {
    return (
      <div className="whatsapp-messenger">
        <div className="auth-required">
          <h3>🔒 Authentication Required</h3>
          <p>Please log in to use WhatsApp messaging features.</p>
          <button onClick={() => window.location.href = '/login'}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="whatsapp-messenger" style={{ paddingTop: "80px", maxWidth: '600px', margin: '0 auto' }}>
      <div className="header">
        <h2>WhatsApp Business API</h2>
        <div className="account-info">
          <div className="account-state">
            <strong>Status:</strong>
            {accountState ? (
              <span className={`state ${accountState.data?.stateInstance}`}>
                {accountState.data?.stateInstance === 'authorized' ? '✅ Connected' : '❌ Not Connected'}
              </span>
            ) : (
              <span>Loading...</span>
            )}
          </div>
          <button
            className="refresh-btn"
            onClick={checkAccountState}
            disabled={loading}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {accountState && accountState.data?.stateInstance !== 'authorized' && (
        <div className="warning">
          <h4>⚠️ WhatsApp Not Authorized</h4>
          <p>Please scan the QR code in your Green-API dashboard to authorize WhatsApp.</p>
          <p><strong>Instance ID:</strong> 7105344520</p>
        </div>
      )}

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'message' ? 'active' : ''}`}
          onClick={() => setActiveTab('message')}
        >
          📝 Send Message
        </button>
        <button
          className={`tab ${activeTab === 'file' ? 'active' : ''}`}
          onClick={() => setActiveTab('file')}
        >
          📎 Send File
        </button>
      </div>

      <div className="form-group">
        <label htmlFor="phones">Phone Number(s) (comma-separated, with country code):</label>
        <input
          type="text"
          id="phones"
          name="phones"
          value={formData.phones}
          onChange={handleInputChange}
          placeholder="e.g., 966565612058,966050153648"
          required
        />
        <small>Format: CountryCode+PhoneNumber (without + or spaces)</small>
      </div>

      {activeTab === 'message' && (
        <form onSubmit={handleSendMessage} className="message-form">
          <div className="form-group">
            <label htmlFor="message">Message:</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Enter your WhatsApp message here..."
              rows="4"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading || !accountState || accountState.data?.stateInstance !== 'authorized'}
            className="send-btn"
          >
            {loading ? '⏳ Sending...' : '📤 Send WhatsApp Message'}
          </button>
        </form>
      )}

      {activeTab === 'file' && (
        <form onSubmit={handleSendFile} className="file-form">
          <div className="form-group">
            <label htmlFor="fileUrl">File URL:</label>
            <input
              type="url"
              id="fileUrl"
              name="fileUrl"
              value={formData.fileUrl}
              onChange={handleInputChange}
              placeholder="https://example.com/document.pdf"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="fileName">File Name:</label>
            <input
              type="text"
              id="fileName"
              name="fileName"
              value={formData.fileName}
              onChange={handleInputChange}
              placeholder="document.pdf"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading || !accountState || accountState.data?.stateInstance !== 'authorized'}
            className="send-btn"
          >
            {loading ? '⏳ Sending...' : '📎 Send File via WhatsApp'}
          </button>
        </form>
      )}

      {response && (
        <div className={`response ${response.success ? 'success' : 'error'}`}>
          <h4>{response.success ? '✅ Success!' : '❌ Error'}</h4>
          <p>{response.message || (response.success ? 'Operation completed successfully' : 'An error occurred')}</p>
          {(response.data || response.error) && (
            <details>
              <summary>Details</summary>
              <pre>{JSON.stringify(response.data || response.error, null, 2)}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default WhatsAppMessenger;
